import { DEFAULT_CHART_TITLE } from 'business/constants';
import type { ChartSettingsT } from 'business/definitions';
import { canCumulateDataTypes } from 'business/definitions';
import { format } from 'date-fns';
import { removeEmptyKeys } from 'helpers/helpers';
import { snakify } from 'helpers/syntaxHelper';
import i18n from 'i18n';
import { serializeFilterTree } from 'modules/decision/pages/Explore/helpers/filterTree';
import type { ChartDataPayload } from 'shared/types';

import { AXIS_SCALES_RESET } from './constants';
import type { SettingsState } from './types';

export const getChartTitle = (settingsChartTitle: string) => {
    if (
        settingsChartTitle === i18n.t(DEFAULT_CHART_TITLE) ||
        settingsChartTitle === ''
    ) {
        return `${i18n.t(DEFAULT_CHART_TITLE)} - ${format(
            new Date(),
            'dd/MM/yyyy',
        )}`;
    }
    return settingsChartTitle;
};

export const getChartData = (
    explore: ExploreQueryT,
    settings: Pick<
        SettingsState,
        'chartTitle' | 'selectedViz' | 'chartSettings'
    >,
): ChartDataPayload => {
    // we compute the title if it is still the default 'Nouveau graphique' one
    const title = getChartTitle(settings.chartTitle);

    return {
        metrics: explore.metrics.filter((e) => e.field),
        dimensions: explore.dimensions.filter((e) => e.field),
        filterExpression: serializeFilterTree(explore.filters),
        vizType: settings.selectedViz,
        title,
        // We clean the settings in case the user set a field to '' or null

        settings: removeEmptyKeys(settings.chartSettings),
    };
};

const shouldResetCumulatedDimension = (
    dimension: ResponseColumnT[],
): boolean => {
    return (
        dimension.filter((d) => d.type && canCumulateDataTypes.includes(d.type))
            .length !== 1
    );
};

export const shouldResetIsStacked = (
    chartSettings: ChartSettingsT,
): boolean => {
    //We should set isStackedBar to False if we are using a secondaryYAxis, and if the number of metrics used is stricly less than 3
    if (chartSettings.isStackedBars && chartSettings.secondaryYAxis) {
        const yLength =
            chartSettings.chartConception?.filter(
                (conception) => conception.chartDimensionKey === 'y',
            ).length || 0;
        return yLength < 3;
    }
    return false;
};

/**
 * Returns true if the chart conception has changed.
 */
const hasConceptionChanged = (
    currentConception: ChartSettingsT['chartConception'],
    nextConception: ChartSettingsT['chartConception'],
): boolean => {
    if (
        !currentConception ||
        !nextConception ||
        currentConception.length !== nextConception.length
    )
        return true;

    const nextKeys = new Set(
        nextConception.map(
            ({ id, chartDimensionKey }) => `${id}:${chartDimensionKey}`,
        ),
    );
    return !currentConception.every(({ id, chartDimensionKey }) =>
        nextKeys.has(`${id}:${chartDimensionKey}`),
    );
};

/**
 * Reinitialized columns settings. It reinitialized metrics cumulated too if we cannot cumulate the dimensions.
 */
export const getResetSettings = (
    queryMetrics: QueryMetricT[],
    columns: ResponseColumnT[],
): Pick<ChartSettingsT, 'metricsCumulated'> => {
    // Columns have changed, we reset some settings to avoid breaking the layout
    const resetSettings: ChartSettingsT = {};

    // Checking if we should reset the cumulated setting
    const metricsFields = queryMetrics.map((m) => m.field);
    const cumulatedDimensions = columns.filter(
        (col) => !metricsFields.includes(snakify(col.key)),
    );
    if (shouldResetCumulatedDimension(cumulatedDimensions)) {
        resetSettings.metricsCumulated = [];
    }
    return resetSettings;
};

/**
 * Returns scales settings to reset when the chart conception or secondaryYAxis changes.
 */
export const getAxisRangeResetSettings = (
    currentSettings: ChartSettingsT,
    newSettings: ChartSettingsT,
): Partial<ChartSettingsT> => {
    if (
        'chartConception' in newSettings &&
        hasConceptionChanged(
            currentSettings.chartConception,
            newSettings.chartConception,
        )
    ) {
        return { ...AXIS_SCALES_RESET };
    }

    const resetSettings: Partial<ChartSettingsT> = {};
    if (
        'secondaryYAxis' in newSettings &&
        newSettings.secondaryYAxis !== currentSettings.secondaryYAxis
    ) {
        resetSettings.secondaryYAxisMinValue = undefined;
        resetSettings.secondaryYAxisMaxValue = undefined;
    }

    return resetSettings;
};
